import { useRef, useState } from "react";
import { Link2, Upload, Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const CHUNK = 8 * 1024 * 1024;

export const VideoSourceField = ({ form, set }) => {
  const fileRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) return toast.error("Only video files are allowed");
    setProgress(0);
    try {
      const { data: init } = await api.post("/videos/uploads", { filename: file.name, content_type: file.type, size: file.size });
      let offset = 0;
      while (offset < file.size) {
        const chunk = file.slice(offset, offset + CHUNK);
        let tries = 0;
        for (;;) {
          try {
            const { data } = await api.put(`/videos/uploads/${init.upload_id}/chunk`, chunk, {
              params: { offset }, headers: { "Content-Type": "application/octet-stream" },
            });
            offset = data.received;
            break;
          } catch (e) {
            if (++tries >= 4 || e.response?.status < 500) throw e;
            await new Promise((r) => setTimeout(r, 1500 * tries));
          }
        }
        setProgress(Math.round((offset / file.size) * 100));
      }
      const { data } = await api.post(`/videos/uploads/${init.upload_id}/complete`);
      set({ video_path: data.path, video_filename: data.filename });
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const tab = (val, Icon, label) => (
    <button type="button" data-testid={`video-source-tab-${val}`} onClick={() => set({ video_type: val })}
      className={`flex-1 h-10 rounded-md flex items-center justify-center gap-2 text-sm font-medium transition-colors ${form.video_type === val ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div>
      <div className="flex gap-1 p-1 rounded-lg bg-[#0B0F17] border border-[#242C3D] mb-4">
        {tab("url", Link2, "Video URL")}
        {tab("upload", Upload, "Upload file")}
      </div>
      {form.video_type === "url" ? (
        <div>
          <input data-testid="campaign-video-url-input" value={form.video_url} onChange={(e) => set({ video_url: e.target.value })}
            placeholder="https://youtube.com/watch?v=…  ·  vimeo.com/…  ·  https://…/video.mp4"
            className="w-full h-11 px-4 rounded-md bg-[#0B0F17] border border-[#242C3D] focus:border-indigo-500 focus:outline-none text-sm" />
          <p className="text-xs text-slate-500 mt-2">Supports YouTube, Vimeo and direct MP4/WebM links. Watch time is tracked for all.</p>
        </div>
      ) : (
        <div>
          <input ref={fileRef} type="file" accept="video/*" className="hidden" data-testid="campaign-video-file-input" onChange={(e) => upload(e.target.files[0])} />
          <button type="button" data-testid="campaign-video-upload-button" disabled={progress !== null} onClick={() => fileRef.current?.click()}
            className="w-full rounded-lg border border-dashed border-[#2f3a50] hover:border-indigo-500 bg-[#0B0F17] py-8 flex flex-col items-center gap-2 transition-colors">
            {progress !== null ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
                <span className="font-mono text-sm" data-testid="campaign-video-upload-progress">Uploading… {progress}%</span>
                <div className="w-48 h-1 rounded bg-[#242C3D] overflow-hidden"><div className="h-full bg-indigo-500 transition-[width]" style={{ width: `${progress}%` }} /></div>
              </>
            ) : form.video_path ? (
              <>
                <Film className="w-5 h-5 text-cyan-400" />
                <span className="text-sm text-slate-200" data-testid="campaign-video-uploaded-name">{form.video_filename || "Uploaded video"}</span>
                <span className="text-xs text-slate-500">Click to replace</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 text-slate-400" />
                <span className="text-sm text-slate-300">Click to upload a video file</span>
                <span className="text-xs text-slate-500">MP4 or WebM · up to 2 GB</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
